import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetConsultants } from "../../../api/masters/consultantAPI";
import { GetNursingStations } from "../../../api/ipd/nursingStationAPI";
import { StoreUser } from "../../../api/utility/userAPI";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

/* -------------------- VALIDATION -------------------- */
const schema = yup.object().shape({
  userName: yup.string().required("User Name is required"),
  password: yup.string().min(6).required("Password is required"),
  blocked: yup.string().required(),
  supervisor: yup.string().required(),
  nature: yup.string(),
  reportPattern: yup.string(),
  charges: yup.string(),
  doctorName: yup.string().nullable(),
  discountAllowed: yup.string(),
  discountUpto: yup.number().nullable(),
  labRegistration: yup.string(),
  opdPrecriptionPrinterPath: yup.string(),
  softwareAutoLogOffIdleTime: yup.number().nullable(),
  exportOptionInPrinting: yup.string(),
  collectionRegisterViewIn: yup.number().nullable(),
  nursingStationId: yup.string().nullable(),
  dischargeBillPrintViewIn: yup.number().nullable(),
});

const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [doctors, setDoctors] = useState([]);
  const [stations, setStations] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      blocked: "No",
      supervisor: "No",
      discountAllowed: "No",
      labRegistration: "No",
      exportOptionInPrinting: "No"
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      const docRes = await GetConsultants();
      const stationRes = await GetNursingStations();
      if (docRes?.success) setDoctors(docRes.data);
      if (stationRes?.success) setStations(stationRes.data);
    };
    fetchData();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await StoreUser(data);

      if (response.success) {
        showToast("success", response.message);
        navigate(`/utility/user/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  const yesNo = ["Yes", "No"];

  return (
    <Fragment>
      <Breadcrumb parent="Users" title="Add User" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add User</h5>
            <Link to={`/utility/user/list`}>
              <button className="btn btn-primary" id="list">List Users</button>
              <UncontrolledTooltip target="list">List Users</UncontrolledTooltip>
            </Link>
          </div>

          <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
            <div className="card-body row">

              {/* User Name */}
              <div className="col-md-4">
                <label>User Name</label>
                <input className="form-control" {...register("userName")} />
                {errors.userName && <p className="text-danger">{errors.userName.message}</p>}
              </div>

              {/* Password */}
              <div className="col-md-4">
                <label>Password</label>
                <input type="password" className="form-control" {...register("password")} />
                {errors.password && <p className="text-danger">{errors.password.message}</p>}
              </div>

              {/* Blocked */}
              <div className="col-md-4">
                <label>Blocked</label>
                <select className="form-control" {...register("blocked")}>
                  {yesNo.map(v => <option key={v}>{v}</option>)}
                </select>
                {errors.blocked && <p className="text-danger">{errors.blocked.message}</p>}
              </div>

              {/* Supervisor */}
              <div className="col-md-4">
                <label>Supervisor</label>
                <select className="form-control" {...register("supervisor")}>
                  {yesNo.map(v => <option key={v}>{v}</option>)}
                </select>
                {errors.supervisor && <p className="text-danger">{errors.supervisor.message}</p>}
              </div>

              {/* Nature */}
              <div className="col-md-4">
                <label>Nature</label>
                <input className="form-control" {...register("nature")} />
                {errors.nature && <p className="text-danger">{errors.nature.message}</p>}
              </div>

              {/* Report Pattern */}
              <div className="col-md-4">
                <label>Report Pattern</label>
                <input className="form-control" {...register("reportPattern")} />
                {errors.reportPattern && <p className="text-danger">{errors.reportPattern.message}</p>}
              </div>

              {/* Charges */}
              <div className="col-md-4">
                <label>Charges</label>
                <input className="form-control" {...register("charges")} />
                {errors.charges && <p className="text-danger">{errors.charges.message}</p>}
              </div>

              {/* Doctor */}
              <div className="col-md-4">
                <label>Doctor</label>
                <select className="form-control" {...register("doctorName")}>
                  <option value="">Select</option>
                  {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                </select>
                {errors.doctorName && <p className="text-danger">{errors.doctorName.message}</p>}
              </div>

              {/* Discount */}
              <div className="col-md-4">
                <label>Discount Allowed</label>
                <select className="form-control" {...register("discountAllowed")}>
                  {yesNo.map(v => <option key={v}>{v}</option>)}
                </select>
                {errors.discountAllowed && <p className="text-danger">{errors.discountAllowed.message}</p>}
              </div>

              <div className="col-md-4">
                <label>Discount Upto</label>
                <input type="number" className="form-control" {...register("discountUpto")} />
                {errors.discountUpto && <p className="text-danger">{errors.discountUpto.message}</p>}
              </div>

              {/* Lab */}
              <div className="col-md-4">
                <label>Lab Registration</label>
                <select className="form-control" {...register("labRegistration")}>
                  {yesNo.map(v => <option key={v}>{v}</option>)}
                </select>
                {errors.labRegistration && <p className="text-danger">{errors.labRegistration.message}</p>}
              </div>

              {/* Printer */}
              <div className="col-md-4">
                <label>OPD Printer Path</label>
                <input className="form-control" {...register("opdPrecriptionPrinterPath")} />
                {errors.opdPrecriptionPrinterPath && <p className="text-danger">{errors.opdPrecriptionPrinterPath.message}</p>}
              </div>

              {/* Idle Time */}
              <div className="col-md-4">
                <label>Auto Logoff Idle Time</label>
                <input type="number" className="form-control" {...register("softwareAutoLogOffIdleTime")} />
                {errors.softwareAutoLogOffIdleTime && <p className="text-danger">{errors.softwareAutoLogOffIdleTime.message}</p>}
              </div>

              {/* Export */}
              <div className="col-md-4">
                <label>Export Option</label>
                <select className="form-control" {...register("exportOptionInPrinting")}>
                  {yesNo.map(v => <option key={v}>{v}</option>)}
                </select>
                {errors.exportOptionInPrinting && <p className="text-danger">{errors.exportOptionInPrinting.message}</p>}
              </div>

              {/* Collection */}
              <div className="col-md-4">
                <label>Collection Register View</label>
                <input type="number" className="form-control" {...register("collectionRegisterViewIn")} />
                {errors.collectionRegisterViewIn && <p className="text-danger">{errors.collectionRegisterViewIn.message}</p>}
              </div>

              {/* Nursing Station */}
              <div className="col-md-4">
                <label>Nursing Station</label>
                <select className="form-control" {...register("nursingStationId")}>
                  <option value="">Select</option>
                  {stations.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                {errors.nursingStationId && <p className="text-danger">{errors.nursingStationId.message}</p>}
              </div>

              {/* Discharge */}
              <div className="col-md-4">
                <label>Discharge Bill Print View</label>
                <input type="number" className="form-control" {...register("dischargeBillPrintViewIn")} />
                {errors.dischargeBillPrintViewIn && <p className="text-danger">{errors.dischargeBillPrintViewIn.message}</p>}
              </div>

            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddUser;
